// @ts-check
/// <reference path="./typedefs.js" />

import { deserialize } from 'deserialize-json-api';
import getProps from 'lodash.get';
import isEmpty from 'lodash.isempty';
import camelCase from 'lodash.camelcase';
import transform from 'lodash.transform';

import HttpClient from '@/utils/clients/clientBookMain';
import ServiceError from '@/utils/errors/ServiceError';
import { buildErrorCollection } from '@/utils/formatters/processingOpenApi';
import {
  formattedPvz,
  formattedPvzRequest,
  processorPvz,
} from '@/services/v1/CheckoutService/formattedPvz';
import { ERROR_SERVER_ERROR } from '@/utils/constants/globalConst';
import { getDateTimeOfString } from '@/utils/dateTime';
import {
  ERROR_CERTIFICATE,
  ERROR_TEXT_NOT_DELIVERIES,
  ERRORS_DELIVERY,
  ERRORS_DELIVERY_SUMMARY,
  ERRORS_SUMMARY,
} from '@/services/v1/CheckoutService/enums';

const typeDiscounts = Object.freeze({
  calculationsTypeForPaymentCard: 'forPayment',
  calculationsTypeForDeliveryWhenPayingOnline: 'forDeliveryWhenPayingOnline',
  discountsTypeBasketItem: 'basketItem',
  discountsTypeDelivery: 'delivery',
});

const valueReplace = value => value.replace(/-/g, '');

/**
 * Возвращает скидку по оплате картой
 * @param {Array} discountCalculations - все возможные скидки
 * @returns {number}
 */
const getDiscountForPaymentCard = (discountCalculations) => {
  let discountForPaymentCard = 0;
  const basketItems = discountCalculations
    .filter(item => item.type === typeDiscounts.discountsTypeBasketItem) || [];

  basketItems.forEach((item) => {
    const calculationsItem = item.calculations
      .filter(itemCalc => itemCalc.discount.type === typeDiscounts.calculationsTypeForPaymentCard)
      || [];
    calculationsItem.forEach((itemCalc) => {
      discountForPaymentCard += itemCalc.amount * item.attributes.quantity || 0;
    });
  });

  return discountForPaymentCard;
};

/**
 * Возвращает скидку по доставке за оплату картой
 * @param {Array} discountCalculations - все возможные скидки
 * @returns {number}
 */
const getDiscountForDeliveryWhenPayingOnline = (discountCalculations) => {
  let discountForDeliveryWhenPayingOnline = 0;

  const itemsDiscountForDeliveryWhenPayingOnline = discountCalculations
    .filter(item => item.type === typeDiscounts.discountsTypeDelivery) || [];

  itemsDiscountForDeliveryWhenPayingOnline.forEach((item) => {
    const calculationsItem = item?.calculations
      .filter(itemCalc => itemCalc.discount.type === typeDiscounts.calculationsTypeForDeliveryWhenPayingOnline)
      || [];
    calculationsItem.forEach((itemCalc) => {
      const amount = itemCalc?.amount ? Number(itemCalc?.amount) : 0;
      discountForDeliveryWhenPayingOnline += amount || 0;
    });
  });

  return discountForDeliveryWhenPayingOnline;
};

/**
 * Возвращает скидки по типам
 * @param {Object} params
 * @param {Array} params.discountCalculations - все возможные скидки
 * @param {number} params.discountPriceAll - сумма общей скидки
 * @param {Array<string>} params.typesOfDiscounts - типы скидок которые хотим получить
 * @returns {{discountOther: number}}
 */
const getDiscounts = (params) => {
  const {
    discountCalculations = [],
    discountPriceAll = 0,
    typesOfDiscounts = [],
  } = params;
  const discounts = {
    discountOther: discountPriceAll,
  };

  typesOfDiscounts.forEach((item) => {
    if (item === typeDiscounts.calculationsTypeForPaymentCard) {
      discounts.discountForPaymentCard = getDiscountForPaymentCard(discountCalculations);
      discounts.discountOther -= discounts.discountForPaymentCard;
    }

    if (item === typeDiscounts.calculationsTypeForDeliveryWhenPayingOnline) {
      discounts.discountForDeliveryWhenPayingOnline = getDiscountForDeliveryWhenPayingOnline(discountCalculations);
    }
  });

  return discounts;
};

const checkDefaultSummery = (data, reject) => {
  const isStrategyBasket = data.strategy === 'basket';
  const isStrategyProduct = data.strategy === 'product';

  if (!isStrategyBasket && !isStrategyProduct) {
    return reject(new ServiceError(ERRORS_DELIVERY_SUMMARY.incorrectStrategy));
  }

  if (isStrategyProduct && !data.productId && typeof data.productId !== 'number') {
    return reject(new ServiceError(ERRORS_DELIVERY_SUMMARY.incorrectProductId));
  }

  if (isStrategyProduct && !data.quantity && typeof data.quantity !== 'number') {
    return reject(new ServiceError(ERRORS_DELIVERY_SUMMARY.incorrectQuantity));
  }

  if (isStrategyProduct && data.coupon && typeof data.coupon !== 'string') {
    return reject(new ServiceError(ERRORS_DELIVERY_SUMMARY.incorrectCoupon));
  }

  if (isStrategyBasket && data.basketId && typeof data.basketId !== 'string') {
    return reject(new ServiceError(ERRORS_DELIVERY_SUMMARY.incorrectBasketId));
  }
};

const checkStrategy = (strategy) => {
  const isStrategyBasket = strategy === 'basket';
  const isStrategyProduct = strategy === 'product';

  return { isStrategyBasket, isStrategyProduct };
};

export default class CheckoutService {
  constructor(config) {
    this.mainHttp = new HttpClient(config);
  }

  /**
   * @param {PayloadGetPvzByCode} payload
   * @return {Promise<unknown>}
   */
  getPvzList(payload) {
    return new Promise((resolve, reject) => {
      if (!payload.location) reject(new ServiceError('[CheckoutService:getPvzList] location обязателен для заполнения'));

      const dataRequest = formattedPvzRequest(payload);

      return this.mainHttp.get('/api/v1/sale/pvz/', dataRequest)
        .then(({ data: { data: dataRes } }) => {
          const { pvzList, pagination } = dataRes;

          return resolve({
            list: pvzList.map(point => formattedPvz(point)),
            pagination: {
              all: pagination.all,
              limit: pagination.limit,
              page: pagination.page,
            },
          });
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);

            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(e));
        });
    });
  }

  /**
   * @param {PayloadGetPvzByCode} payload
   * @return {Promise<unknown>}
   */
  getPvzByCode(payload) {
    return new Promise((resolve, reject) => {
      if (!payload.location) reject(new ServiceError('[CheckoutService:getPvzByCode] location обязателен для заполнения'));
      if (!payload.code) reject(new ServiceError('[CheckoutService:getPvzByCode] code обязателен для заполнения'));

      const dataRequest = formattedPvzRequest(payload);

      return this.mainHttp.get(`/api/v1/sale/pvz/${payload.code}/`, dataRequest)
        .then(({ data: { data: dataRes } }) => {
          const { pvzInfo } = dataRes;
          return resolve({
            list: [formattedPvz(pvzInfo)],
          });
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);

            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(e));
        });
    });
  }

  /**
   * Возвращает список станций метро
   * @param {PayloadGetMetro} payload
   * @returns {Promise<Object<string, ItemMetro>>}
   */
  getMetro({ location }) {
    return new Promise((resolve, reject) => {
      if (!location) reject(new ServiceError('[CheckoutService:getMetro] location обязателен для заполнения'));

      return this.mainHttp.get('/api/v1/sale/metro/', {
        LOCATION: location,
      })
        .then(({ data: { data: dataRes } }) => {
          const { metroStations } = dataRes;
          const formattedStations = metroStations.reduce((stations, station) => {
            stations[station.id] = {
              name: station.name,
              coordinates: [station.latitude, station.longitude],
              metroId: station.id,
              colorBranch: station.color,
            };

            return stations;
          }, {});

          return resolve(formattedStations);
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);

            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(e));
        });
    });
  }

  /**
   * @typedef {Object} PayloadGetOrderDeliveries
   * @property {boolean} isPreOrder
   * @property {string} location
   * @property {string} coordinates
   * @property {string} index
   * @property {(1 | 0)} personType
   * @property {number|string} activePaySystemId
   */

  /**
   * @param {PayloadGetOrderDeliveries} data
   * @return {Promise<unknown>}
   */
  getOrderDeliveries({
    isPreOrder,
    location,
    coordinates,
    index,
    personType,
    activePaySystemId,
  }) {
    return new Promise((resolve, reject) => {
      const basketMode = isPreOrder === true ? 'preorder' : undefined;
      const dataRequest = {
        LOCATION: location,
        PERSON_TYPE_ID: personType,
        BASKET_MODE: basketMode,
      };

      if (!isEmpty(coordinates)) {
        Object.assign(dataRequest, {
          PROPERTY_ADDRESS_COORDINATES: coordinates,
        });
      }

      if (index) {
        Object.assign(dataRequest, {
          PROPERTY_ZIP: index,
        });
      }

      if (activePaySystemId) {
        Object.assign(dataRequest, {
          PAY_SYSTEM_ID: activePaySystemId,
        });
      }

      return this.mainHttp.get('/api/v1/sale/order/delivery/', dataRequest)
        .then(({ data }) => {
          if (data.status === 'OK') {
            const { data: { deliveryByGroup } } = data;
            const newObj = {};
            Object.keys(deliveryByGroup).forEach((item) => {
              const group = deliveryByGroup[item];
              newObj[item] = {
                title: group.title,
                price: group.deliveryMinPrice,
                priceFormatted: group.deliveryMinPriceFormat,
                checked: group.checked === 'Y',
                rows: group.rows.reduce((rows, row) => {
                  const deliveryDate = row?.deliveryDate || {};

                  const rowData = {
                    id: row.id,
                    name: row.name,
                    code: row.code,
                    codeFormatted: camelCase(row.code),
                    price: row.price,
                    priceFormatted: row.priceFormated,
                    deliveryTimePeriods: row.deliveryTimePeriods,
                    description: row.description,
                  };

                  if (!isEmpty(deliveryDate)) {
                    Object.assign(rowData, {
                      date: deliveryDate.dateFormat,
                      dateFormatted: deliveryDate.date,
                      availableDates: deliveryDate.availableDates,
                      days: deliveryDate.days,
                    });
                  }
                  rows[row.code] = rowData;
                  return rows;
                }, {}),
              };
            });
            resolve(newObj);
          }

          const errorObj = {
            code: data.code,
            message: String(data.code) === ERRORS_DELIVERY.emptyDelivery.code
              ? ERRORS_DELIVERY.emptyDelivery.message : ERRORS_DELIVERY.otherError.message,
          };
          reject(errorObj);
        })
        .catch((error) => {
          if (error.response) {
            const newErrorData = error.response.status === 500 ? { code: error.response.status } : { ...error.response.data };
            if (newErrorData?.code === 0) {
              newErrorData.code = error.response.status;
            }
            const errors = buildErrorCollection(newErrorData, ERRORS_DELIVERY);
            // знаем точно что ожидаем одну ошибку
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(error);
        });
    });
  }

  getFreeDelivery(location) {
    return new Promise((resolve, reject) => this.mainHttp.get('/api/v1/sale/order/delivery/free/', {
      LOCATION: location,
    })
      .then(({ data: { data: dataRes } }) => resolve(dataRes))
      .catch((e) => {
        if (e.response) {
          if (e.response.status === 500) {
            return reject(new ServiceError(ERROR_SERVER_ERROR));
          }
          const errors = buildErrorCollection(e.response.data);
          const [firstError] = errors;
          return reject(new ServiceError(firstError));
        }
        return reject(new ServiceError(e));
      }));
  }

  /**
   * Возвращает информацию о прошлом заказе
   * @param {Object} payload
   * @param {boolean} [payload.isPreorder=false] - получить предыдущий предзаказ или обычный заказ
   * @return {Promise<PreviousOrder>}
   */
  checkPreviousOrder({ isPreorder = false }) {
    return new Promise((resolve, reject) => this.mainHttp.get('/api/v1/sale/initial/', {
      preOrderMode: isPreorder ? 'preorder' : null,
    })
      .then(({ data: { data: { previousOrderData, currentBasketIsEmpty } } }) => {
        if (!previousOrderData) {
          return reject(new ServiceError({ message: 'Предыдущего заказа не найдено', code: '200' }));
        }

        const properties = previousOrderData?.properties || {};
        const coordinatesArray = properties?.addressCoordinates?.value?.split(', ') || [];
        const bonusPay = previousOrderData.bonuspay;
        const isBlocked = bonusPay?.bonusPayBlocked?.isBlocked ?? true;
        const maxPay = bonusPay?.bonuspayData?.maxpay ?? 0;
        const isActiveLoyalty = bonusPay?.isPlEnabled ?? false;

        return resolve({
          isEmptyBasket: currentBasketIsEmpty,
          previousOrder: {
            personType: previousOrderData.personTypeId || 1,
            activeDelivery: previousOrderData.deliveryGroupType,
            deliveryId: previousOrderData.deliveryId,
            deliveryCode: previousOrderData.deliveryCode,
            pvzCode: properties.pvz.value,
            paySystemId: previousOrderData.paySystemId,
            paySystemName: previousOrderData.paySystemName,
            lastName: properties.lastName.value,
            firstName: properties.name.value,
            secondName: properties.secondName.value,
            email: properties.email.value,
            phone: properties.personalPhone.value,
            location: properties.location.value,
            cityName: properties.city.value,
            fullAddress: properties.address.value,
            street: properties.street.value,
            house: properties.home.value,
            block: properties.corpus.value,
            apartment: properties.apartment.value,
            index: properties.zip.value,
            coordinates: coordinatesArray.length ? coordinatesArray : null,
            useBonusPay: isActiveLoyalty && !isBlocked ? maxPay > 0 : false,
          },
          bonusPay: {
            isBlocked,
            maxPay,
            isActiveLoyalty,
          },
        });
      })
      .catch((e) => {
        if (e.response) {
          if (e.response.status === 500) {
            return reject(new ServiceError(ERROR_SERVER_ERROR));
          }
          const errors = buildErrorCollection(e.response.data);
          const [firstError] = errors;
          return reject(new ServiceError(firstError));
        }
        return reject(new ServiceError(e));
      }));
  }

  calculateOrder(payload) {
    return new Promise((resolve, reject) => {
      const dataRequest = {
        LOCATION: payload.location,
        DELIVERY_ID: payload.deliveryId,
        PAY_SYSTEM_ID: payload.paySystemId,
        USE_BONUSPAY: payload.useBonusPay ? 'Y' : 'N',
        PVZ_CODE: payload.pvzCode,
        PROPERTY_CITY: payload.cityName,
        PROPERTY_STREET: payload.street,
        PROPERTY_HOME: payload.house,
        PROPERTY_CORPUS: payload.block,
        PROPERTY_APARTMENT: payload.apartment,
        PROPERTY_ZIP: payload.index,
        USER_DESCRIPTION: payload.message,
        PROPERTY_ADDRESS_FULL: payload.fullAddress,
        PERSON_TYPE_ID: payload.personType,
        BASKET_MODE: payload.isPreorder ? 'preorder' : null,
      };

      if (payload.email) {
        Object.assign(dataRequest, {
          PROPERTY_EMAIL: payload.email,
        });
      }

      if (payload.firstName) {
        Object.assign(dataRequest, {
          PROPERTY_NAME: payload.firstName,
        });
      }

      if (payload.lastName) {
        Object.assign(dataRequest, {
          PROPERTY_LAST_NAME: payload.lastName,
        });
      }

      if (payload.secondName) {
        Object.assign(dataRequest, {
          PROPERTY_SECOND_NAME: payload.secondName,
        });
      }

      if (payload.phone) {
        Object.assign(dataRequest, {
          PROPERTY_PERSONAL_PHONE: payload.phone,
        });
      }

      const coordinates = payload?.coordinates ?? null;
      if (coordinates) {
        let coordinatesData = coordinates;
        if (Array.isArray(coordinates)) {
          coordinatesData = coordinates.join(',');
        }
        Object.assign(dataRequest, {
          PROPERTY_ADDRESS_COORDINATES: coordinatesData,
        });
      }

      const deliveryDate = getProps(payload, 'deliveryDate', null);
      if (!isEmpty(deliveryDate)) {
        Object.assign(dataRequest, {
          PROPERTY_DELIVERY_DATE: deliveryDate,
        });
      }

      const certificate = getProps(payload, 'certificate', null);
      if (!isEmpty(certificate)) {
        Object.assign(dataRequest, {
          CERTIFICATE: certificate,
        });
      }

      if (!isEmpty(payload.period)) {
        Object.assign(dataRequest, {
          PROPERTY_DELIVERY_INTERVAL: payload.period,
        });
      }

      return this.mainHttp.post('/api/v1/sale/order/calculate/', dataRequest, { isFormData: true })
        .then(({ data: { data: { delivery, total } } }) => {
          const deliveryDateFormatted = delivery.deliveryDate ? getDateTimeOfString(delivery.deliveryDate, 'd MMMM') : null;

          return resolve({
            deliveryDate: delivery.deliveryDate,
            deliveryDateFormatted,
            deliveryDays: delivery.deliveryDays,
            deliveryPrice: total.deliveryPrice,
            amountPayableOrder: total.amountPayableOrder,
          });
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 500) {
              return reject(new ServiceError(ERROR_SERVER_ERROR));
            }
            const errors = buildErrorCollection(error.response.data);
            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(error));
        });
    });
  }

  saleOrderTotal(payload) {
    return new Promise((resolve, reject) => {
      const addressList = [payload.index, payload.cityName, payload.street, payload.house, payload.block, payload.number]
        .filter(part => part);
      const basketMode = payload.isPreOrder ? 'preorder' : undefined;
      const dataRequest = {
        LOCATION: payload.location,
        DELIVERY_ID: payload.deliveryId,
        PVZ_CODE: payload.pvzCode,
        PROPERTY_CITY: payload.cityName,
        PROPERTY_STREET: payload.street,
        PROPERTY_HOME: payload.house,
        PROPERTY_ZIP: payload.index,
        PROPERTY_CORPUS: payload.block,
        PROPERTY_ADDRESS_FULL: payload.fullAddress || addressList.join(', '),
        USE_BONUSPAY: payload.useBonusPay ? 'Y' : 'N',
        PERSON_TYPE_ID: payload.personType,
        BASKET_MODE: basketMode,
      };

      if (payload.useBonusPay) {
        Object.assign(dataRequest, {
          BONUS_PAY: payload.bonusPay,
        });
      }

      if (!isEmpty(payload.index)) {
        Object.assign(dataRequest, {
          PROPERTY_ZIP: payload.index,
        });
      }

      if (!isEmpty(payload.number)) {
        Object.assign(dataRequest, {
          PROPERTY_APARTMENT: payload.number,
        });
      }

      if (!isEmpty(payload.certificate)) {
        Object.assign(dataRequest, {
          CERTIFICATE: payload.certificate,
        });
      }

      if (!isEmpty(payload.geoLat) && !isEmpty(payload.geoLon)) {
        Object.assign(dataRequest, {
          PROPERTY_ADDRESS_COORDINATES: String([payload.geoLat, payload.geoLon]
            .filter(part => part).join(', ')),
        });
      }

      if (!isEmpty(payload.coordinates)) {
        Object.assign(dataRequest, {
          PROPERTY_ADDRESS_COORDINATES: payload.coordinates,
        });
      }

      if (payload.paySystemId) {
        Object.assign(dataRequest, {
          PAY_SYSTEM_ID: payload.paySystemId,
        });
      }

      return this.mainHttp.get('/api/v1/sale/order/total/', dataRequest)
        .then(({ data: { data } }) => {
          const {
            orderTotal,
            bonusInfo,
            certificateProp,
            loyaltyProgram,
          } = data;
          const { items: discountCalculations = [] } = data.discountCalculations;
          const { discountPrice = 0 } = orderTotal;

          const typesOfDiscounts = [
            typeDiscounts.calculationsTypeForDeliveryWhenPayingOnline,
          ];
          const discounts = getDiscounts({
            discountCalculations,
            discountPriceAll: discountPrice,
            typesOfDiscounts,
          });

          resolve({
            summary: {
              count: orderTotal.itemsCount,
              weight: orderTotal.basketWeight,
              weightFormatted: orderTotal.basketWeightFormat,
              priceWithoutDelivery: orderTotal.orderPriceWithoutDelivery,
              deliveryPrice: orderTotal.deliveryPrice,
              discountAll: discountPrice,
              discountByTypes: discounts,
              loyaltyProgram: loyaltyProgram?.bonuspay?.bonuspayData?.currentpay || 0,
              certificatePay: certificateProp?.certificatePay || null,
              amountPayableOrder: orderTotal.amountPayableOrder,
              price: orderTotal.orderPrice,
            },
            bonuses: {
              mnogoRu: {
                value: bonusInfo?.mnogoRu?.bonuses || null,
                valueFormatted: bonusInfo?.mnogoRu?.bonusesText || null,
              },
            },
            bonusPay: {
              isBlocked: loyaltyProgram?.bonuspay?.bonusPayBlocked?.isBlocked ?? true,
              maxPay: loyaltyProgram.bonuspay?.bonuspayData?.maxpay ?? 0,
              isActiveLoyalty: loyaltyProgram.bonuspay?.isPlEnabled ?? false,
            },
          });
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 500) {
              return reject(new ServiceError(ERROR_SERVER_ERROR));
            }
            const errors = buildErrorCollection(error.response.data);
            const [firstError] = errors;
            reject(new ServiceError(firstError));
          }
          reject(new ServiceError(error));
        });
    });
  }

  saveOrder(payload) {
    return new Promise((resolve, reject) => {
      const dataRequest = {
        LOCATION: payload.location,
        DELIVERY_ID: payload.deliveryId,
        PAY_SYSTEM_ID: payload.paySystemId,
        USE_BONUSPAY: payload.useBonusPay ? 'Y' : 'N',
        PVZ_CODE: payload.pvzCode,
        PROPERTY_NAME: payload.firstName,
        PROPERTY_LAST_NAME: payload.lastName,
        PROPERTY_SECOND_NAME: payload.secondName,
        PROPERTY_EMAIL: payload.email,
        PROPERTY_PERSONAL_PHONE: payload.phone,
        PROPERTY_CITY: payload.cityName,
        PROPERTY_STREET: payload.street,
        PROPERTY_HOME: payload.house,
        PROPERTY_CORPUS: payload.block,
        PROPERTY_APARTMENT: payload.apartment,
        PROPERTY_ZIP: payload.index,
        USER_DESCRIPTION: payload.message,
        PROPERTY_ADDRESS_FULL: payload.fullAddress,
        PERSON_TYPE_ID: payload.personType,
        BASKET_MODE: payload.isPreOrder ? 'preorder' : null,
      };

      if (payload.clientId) {
        Object.assign(dataRequest, {
          GA_CLIENT_ID: payload.clientId,
        });
      }

      if (payload.source) {
        Object.assign(dataRequest, {
          UTM: { SOURCE: payload.source },
        });
      }

      const coordinates = payload?.coordinates ?? null;
      if (coordinates) {
        let coordinatesData = coordinates;
        if (Array.isArray(coordinates)) {
          coordinatesData = coordinates.join(',');
        }
        Object.assign(dataRequest, {
          PROPERTY_ADDRESS_COORDINATES: coordinatesData,
        });
      }

      const deliveryDate = getProps(payload, 'deliveryDate', null);
      if (!isEmpty(deliveryDate)) {
        Object.assign(dataRequest, {
          PROPERTY_DELIVERY_DATE: deliveryDate,
        });
      }

      if (payload.personType === 2) {
        Object.assign(dataRequest, {
          PROPERTY_INN: payload.inn,
          PROPERTY_ORGANIZATION_NAME: payload.organizationName,
          PROPERTY_LEGAL_ADDRESS: payload.legalAddress,
          PROPERTY_KPP: payload.kpp,
          PROPERTY_BIK: payload.bik,
          PROPERTY_CORRESPONDENT_ACCOUNT: payload.correspondentAccount,
          PROPERTY_CHECKING_ACCOUNT: payload.checkingAccount,
          PROPERTY_BANK_NAME: payload.bankName,
        });
      }

      const certificate = getProps(payload, 'certificate', null);
      if (!isEmpty(certificate)) {
        Object.assign(dataRequest, {
          CERTIFICATE: certificate,
        });
      }

      if (!isEmpty(payload.period)) {
        Object.assign(dataRequest, {
          PROPERTY_DELIVERY_INTERVAL: payload.period,
        });
      }

      return this.mainHttp.post('/api/v1/sale/order/save/', dataRequest, { isFormData: true })
        .then(({ data: { data: { orderId = null, ddl } } }) => {
          resolve({
            orderId,
            ddl,
          });
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 500) {
              reject(new ServiceError(ERROR_SERVER_ERROR));
            }
            const errors = buildErrorCollection(error.response.data);
            const [firstError] = errors;
            reject(new ServiceError(firstError));
          }
          reject(new ServiceError(error));
        });
    });
  }

  fetchPaySystems(payload) {
    return new Promise((resolve, reject) => {
      const basketMode = payload.isPreOrder ? 'preorder' : undefined;
      const personType = payload.personType || 1;
      const dataRequest = {
        LOCATION: payload.location,
        DELIVERY_ID: payload.deliveryId,
        PVZ_CODE: payload.pvzCode,
        PERSON_TYPE_ID: personType,
        BASKET_MODE: basketMode,
      };

      if (payload.email) {
        Object.assign(dataRequest, {
          PROPERTY_EMAIL: payload.email,
        });
      }

      if (payload.firstName) {
        Object.assign(dataRequest, {
          PROPERTY_FIRST_NAME: payload.firstName,
        });
      }

      if (payload.lastName) {
        Object.assign(dataRequest, {
          PROPERTY_LAST_NAME: payload.lastName,
        });
      }

      if (payload.secondName) {
        Object.assign(dataRequest, {
          PROPERTY_SECOND_NAME: payload.secondName,
        });
      }

      if (payload.phone) {
        Object.assign(dataRequest, {
          PROPERTY_PERSONAL_PHONE: payload.phone,
        });
      }

      if (payload.deliveryId) {
        Object.assign(dataRequest, {
          DELIVERY_ID: payload.deliveryId,
        });
      }

      if (payload.pvzCode) {
        Object.assign(dataRequest, {
          PVZ_CODE: payload.pvzCode,
        });
      }

      // если присутствуют координаты
      if (!isEmpty(payload.coordinates)) {
        Object.assign(dataRequest, {
          PROPERTY_ADDRESS_COORDINATES: payload.coordinates,
        });
      }

      // если присутствует поле сертификата
      if (payload.certificate && payload.certificate.length) {
        Object.assign(dataRequest, {
          CERTIFICATE: payload.certificate,
        });
      }

      return this.mainHttp.get('/api/v1/sale/order/paysystem/', dataRequest)
        .then(({ data }) => {
          if (data.status === 'OK') {
            const { data: { paySystem, bonuspay, certificatePay } } = data;

            const returnData = {
              paySystems: transform(paySystem, (result, item) => {
                result.push({
                  name: item.name,
                  code: item.code,
                  description: item.description,
                  id: item.paySystemId,
                });
              }, []),
              showCertificate: certificatePay?.enable || false,
            };

            if (!isEmpty(bonuspay)) {
              Object.assign(returnData, {
                bonusPay: {
                  isBlocked: bonuspay?.bonusPayBlocked?.isBlocked ?? true,
                  maxPay: bonuspay?.bonuspayData?.maxpay ?? 0,
                  isActiveLoyalty: bonuspay?.isPlEnabled ?? false,
                },
              });
            }

            resolve(returnData);
          }

          const errorObj = {
            code: data.code,
            message: ERROR_TEXT_NOT_DELIVERIES,
          };
          reject(errorObj);
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 500) {
              return reject(new ServiceError(ERROR_TEXT_NOT_DELIVERIES));
            }

            const newErrorData = error.status === 502 ? { CODE: error.status } : { ...error.response.data };
            if (newErrorData?.CODE === 0) {
              newErrorData.CODE = error.status;
            }
            const errors = buildErrorCollection(newErrorData);
            const [firstError] = errors;
            reject(new ServiceError(firstError));
          }
          reject(new ServiceError(error));
        });
    });
  }

  setCertificate(value, maxLengthValue = 12) {
    return new Promise((resolve, reject) => {
      const rowValue = valueReplace(value);
      if (rowValue.length === 0) {
        return reject(new ServiceError(ERROR_CERTIFICATE.emptyNumber.message));
      }
      if (rowValue.length > 0 && rowValue.length < maxLengthValue) {
        return reject(new ServiceError(ERROR_CERTIFICATE.unCorrectNumber.message.replace('{maxLengthValue}', String(maxLengthValue))));
      }

      this.mainHttp.get(`/api/v1/sale/certificate/${value}/`)
        .then(({ data: { data } }) => resolve({
          activeFrom: data.dateActiveFrom || null,
          activeTo: data.dateActiveTo || null,
          type: data.type || null,
          sum: data.value || null,
        }))
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 500) {
              reject(new ServiceError(ERROR_SERVER_ERROR));
            }
            const errors = buildErrorCollection(error.response.data, ERROR_CERTIFICATE);
            const [firstError] = errors;
            reject(new ServiceError(firstError));
          }
          reject(new ServiceError(error));
        });
    });
  }

  fetchPvzOrderHistory({
    limit = 3,
    page = 1,
    isPreorder = false,
    location,
    deliveryMethods,
    paySystemId,
  }) {
    return new Promise((resolve, reject) => {
      const BASKET_MODE = isPreorder ? 'preorder' : 'basket';
      const dataRequest = {
        LOCATION: location,
        LIMIT: limit,
        PAGE: page,
        DELIVERY_METHODS: deliveryMethods,
        BASKET_MODE,
      };
      if (paySystemId) {
        Object.assign(dataRequest, {
          PAY_SYSTEM_ID: paySystemId,
        });
      }
      return this.mainHttp.get('/api/v1/sale/order/used-pvz-order-history/', dataRequest)
        .then(({ data: { data: { pvzList } } }) => resolve(processorPvz(pvzList)))
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 500) {
              return reject(new ServiceError(ERROR_SERVER_ERROR));
            }
            const errors = buildErrorCollection(error.response.data);
            const [firstError] = errors;
            reject(new ServiceError(firstError));
          }
          reject(new ServiceError(error));
        });
    });
  }

  /**
   * Возвращает сводную информацию о доставках
   * @param {Object} payload
   * @param {string} payload.locationCode
   * @param {Array<string>} [payload.locationCoordinates]
   * @param {('product' | 'basket')} [payload.strategy='basket']
   * @param {number} [payload.productId]
   * @param {number} [payload.quantity=1]
   * @param {string} [payload.coupon]
   * @param {string} [payload.basketId]
   * @return {Promise<unknown>}
   */
  fetchDeliverySummary({
    locationCode,
    locationCoordinates,
    strategy = 'basket',
    productId,
    quantity = 1,
    coupon,
    basketId,
  }) {
    return new Promise((resolve, reject) => {
      const { isStrategyBasket, isStrategyProduct } = checkStrategy(strategy);
      checkDefaultSummery({
        strategy,
        productId,
        quantity,
        coupon,
        basketId,
      }, reject);

      const dataRequest = {
        from: { strategy },
        location: { code: locationCode, coordinates: null },
      };

      if (locationCoordinates && Array.isArray(locationCoordinates)) {
        const [latitude, longitude] = locationCoordinates;
        dataRequest.location.coordinates = { latitude, longitude };
      }

      // если стратегия товар, то нужно сразу заполнить обязательные поля
      if (isStrategyProduct) {
        dataRequest.from.payload = { productId, quantity };
      }

      // если стратегия товар и заполнен купон, то нужно заполнить купон
      if (isStrategyProduct && coupon) {
        dataRequest.from.payload = { coupon };
      }

      // если стратегия корзина, то нужно сразу заполнить обязательные поля
      if (isStrategyBasket) {
        dataRequest.from.payload = { basketId };
      }

      this.mainHttp.post('/api/v1/checkout/delivery-summary/', dataRequest)
        .then(({ data: { data: dataRes } }) => {
          const { data } = deserialize(dataRes);
          resolve(data);
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 500) {
              return reject(new ServiceError(ERROR_SERVER_ERROR));
            }

            const errors = buildErrorCollection(error.response.data, ERRORS_DELIVERY_SUMMARY);
            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(error));
        });
    });
  }

  /**
   * Возвращает сводную информацию
   * @param {Object} payload
   * @param {string} payload.locationCode
   * @param {Array<string>} [payload.locationCoordinates]
   * @param {('product' | 'basket')} [payload.strategy='basket']
   * @param {number} [payload.productId]
   * @param {number} [payload.quantity=1]
   * @param {string} [payload.coupon]
   * @param {string} [payload.basketId]
   * @param {('shipment' | 'payment' | 'preview')} [payload.stageName='shipment']
   * @param {boolean} [payload.isLegal=false]
   * @param {number} [payload.paySystemId=31]
   * @param {Array<number>} [payload.recommendation]
   * @return {Promise<unknown>}
   */
  fetchSummary({
    locationCode,
    locationCoordinates,
    strategy = 'basket',
    productId,
    quantity = 1,
    coupon,
    basketId,
    stageName = 'shipment',
    isLegal = false,
    paySystemId = 31,
    recommendation = [],
  }) {
    return new Promise((resolve, reject) => {
      const { isStrategyBasket, isStrategyProduct } = checkStrategy(strategy);
      checkDefaultSummery({
        strategy,
        productId,
        quantity,
        coupon,
        basketId,
      }, reject);

      const customerType = isLegal ? 'legal' : 'individual';

      const dataRequest = {
        from: { strategy },
        stage: {
          name: stageName,
          payload: {
            shipment: {
              location: { code: locationCode, coordinates: null },
            },
            customer: {
              rightType: customerType,
              attributes: null,
            },
            payment: {
              paySystemId,
            },
          },
        },
        recommendation: {
          productId: recommendation,
        },
      };

      // наполняем коорзинаты местоположения
      if (locationCoordinates && Array.isArray(locationCoordinates)) {
        const [latitude, longitude] = locationCoordinates;
        dataRequest.stage.shipment.location.coordinates = { latitude, longitude };
      }

      // если стратегия товар, то нужно сразу заполнить обязательные поля
      if (isStrategyProduct) {
        dataRequest.from.payload = { productId, quantity };
      }

      // если стратегия товар и заполнен купон, то нужно заполнить купон
      if (isStrategyProduct && coupon) {
        dataRequest.from.payload = { coupon };
      }

      // если стратегия корзина, то нужно сразу заполнить обязательные поля
      if (isStrategyBasket) {
        dataRequest.from.payload = { basketId };
      }

      this.mainHttp.post('/api/v1/checkout/summary/', dataRequest)
        .then(({ data: { data: dataRes } }) => {
          const { data } = deserialize(dataRes);
          resolve(data);
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 500) {
              return reject(new ServiceError(ERROR_SERVER_ERROR));
            }

            const errors = buildErrorCollection(error.response.data, ERRORS_SUMMARY);
            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(error));
        });
    });
  }

  fetchPotentialGifts({
    locationCode,
    locationCoordinates,
    strategy = 'basket',
    productId,
    quantity = 1,
    coupon,
    basketId,
    isLegal = false,
    paySystemId = 31,
    isGetProduct = true,
  }) {
    return new Promise((resolve, reject) => {
      const { isStrategyBasket, isStrategyProduct } = checkStrategy(strategy);
      checkDefaultSummery({
        strategy,
        productId,
        quantity,
        coupon,
        basketId,
      }, reject);

      const customerType = isLegal ? 'legal' : 'individual';

      const dataRequest = {
        from: { strategy },
        shipment: {
          location: { code: locationCode, coordinates: null },
        },
        customer: {
          rightType: customerType,
          attributes: null,
        },
        payment: {
          paySystemId,
        },
      };

      let urlRequest = '/api/v1/checkout/potential-gifts/';

      // наполняем координат местоположения
      if (locationCoordinates && Array.isArray(locationCoordinates)) {
        const [latitude, longitude] = locationCoordinates;
        dataRequest.shipment.location.coordinates = { latitude, longitude };
      }

      // если стратегия товар, то нужно сразу заполнить обязательные поля
      if (isStrategyProduct) {
        dataRequest.from.payload = { productId, quantity };
      }

      // если стратегия товар и заполнен купон, то нужно заполнить купон
      if (isStrategyProduct && coupon) {
        dataRequest.from.payload = { coupon };
      }

      // если стратегия корзина, то нужно сразу заполнить обязательные поля
      if (isStrategyBasket) {
        dataRequest.from.payload = { basketId };
      }

      if (isGetProduct) {
        urlRequest += '?include=product';
      }

      this.mainHttp.post(urlRequest, dataRequest)
        .then(({ data: { data: dataRes } }) => {
          const { data } = deserialize(dataRes);
          resolve(data);
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 500) {
              return reject(new ServiceError(ERROR_SERVER_ERROR));
            }

            const errors = buildErrorCollection(error.response.data, ERRORS_SUMMARY);
            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(error));
        });
    });
  }
}
